import { getStatusByTTCode, isVehicleDisabled } from '@utils/sales';
import defaultTo from 'lodash/defaultTo';
import {
  getIFSModelSeriesCodeFromPimCode,
  IModelSeriesMapping,
  invokeProvidedBrandSpecificCode,
  IOrder,
  IUniqueModelCodeAndSkus,
  MAKE,
} from 'next-common';
import { formatDate, formatNumber, generateExactMatchCode, getEnumKeyFromValue } from 'sales-common-mapper';
import {
  BosBrand,
  IFilterResult,
  IFilterResultWithCount,
  IFilterValueWithCount,
  IReducedVehicleResponse,
  IVehicleSearchOptions,
  IVehicleSearchResponse,
  Order,
  OrderByField,
  SortDirection,
  SortOrder,
  VehicleCondition,
  VehicleStockStatus,
} from 'sales-types-common';
import {
  Dealer,
  FILTER_FETCH_STEP,
  FilterCount,
  FilterFetchStrategy,
  FilterFetchStrategyParams,
  getTTCodeStatusName,
  IBrandOption,
  IDynamicListStockData,
  ISortDirection,
  IStockFilterConfig,
  IStockFilterOptions,
  IVehicleCountsPayload,
  IVehicleFiltersWithCounts,
  IVehicleSelectedFilters,
  VehicleFilterType,
} from '../../common';
import {
  vehicleListCondition,
  vehicleListDropToFilterKeyMap,
  vehicleListStatus,
  dropdownToFilterKeyMap,
} from './const';
import { isEmpty } from 'lodash';

const getActiveFilterKeys = (
  filter: IStockFilterConfig | null | undefined,
  filterKeyToExclude: string,
  filterKeyMap: Record<string, string>
): string[] => {
  const activeKeys: string[] = [];

  if (!filter || typeof filter !== 'object') {
    return [filterKeyToExclude];
  }

  for (const [dropdownKey, filterKey] of Object.entries(filterKeyMap)) {
    const filterValue = filter[dropdownKey as keyof IStockFilterConfig];

    if (Array.isArray(filterValue) && filterValue?.length > 0) {
      activeKeys.push(filterKey);
    }
  }

  if (!activeKeys.includes(filterKeyToExclude)) {
    activeKeys.push(filterKeyToExclude);
  }

  return activeKeys;
};

export const createStockListFilterRequestData = (
  filter: IStockFilterConfig,
  openedDropdown: string,
  includeReserved?: boolean
) => {
  const data: Record<string, any> = {};

  const filterKeyToExclude = vehicleListDropToFilterKeyMap[openedDropdown];

  if (filterKeyToExclude !== 'brand') {
    data.brand = mapValues(filter?.brands ?? []);
  }

  if (filterKeyToExclude !== 'modelSeries') {
    data.modelSeries = mapValues(filter?.modelSeries ?? []);
  }

  if (filterKeyToExclude !== 'modelId') {
    data.modelId = mapValues(filter?.modelIds ?? []);
  }
  if (filterKeyToExclude !== 'colorCode') {
    data.colorCode = mapValues(filter?.colorIds ?? []);
  }
  if (filterKeyToExclude !== 'interiorCode') {
    data.interiorCode = mapValues(filter?.interiorIds ?? []);
  }
  if (filterKeyToExclude !== 'options') {
    data.options = mapValues(filter?.optionIdsIncluded ?? []);
  }
  if (filterKeyToExclude !== 'locationId') {
    data.locationId = mapValues(filter?.locationIds ?? []);
  }

  data.filterTypes = getActiveFilterKeys(filter, filterKeyToExclude, vehicleListDropToFilterKeyMap);

  data.vehicleCondition = vehicleListCondition;
  data.status = getVehicleListStatusFilter(includeReserved);

  return { filters: data };
};

export const newVehicleListOrderMapper = (sortDirection: ISortDirection): SortOrder[] => {
  const order = [];

  if (sortDirection.productionDate) {
    order.push({
      order: 1,
      fieldName: OrderByField.PRODUCTIONDATE,
      method: sortDirection.productionDate === SortDirection.Asc ? 'ASC' : 'DESC',
    });
  } else if (sortDirection.trackTraceCode) {
    order.push({
      order: 1,
      fieldName: OrderByField.TRACKTRACECODE,
      method: sortDirection.trackTraceCode === SortDirection.Asc ? 'ASC' : 'DESC',
    });
  } else if (sortDirection.pris) {
    order.push({
      order: 1,
      fieldName: OrderByField.CAMPAIGNPRICE,
      method: sortDirection.pris === SortDirection.Asc ? 'ASC' : 'DESC',
    });
  } else if (sortDirection.modelDescription) {
    order.push({
      order: 1,
      fieldName: OrderByField.MODEL,
      method: sortDirection.pris === SortDirection.Asc ? 'ASC' : 'DESC',
    });
  }

  return order;
};

export function filterBosBrandOption(brands: IBrandOption[]): any {
  const validValues = new Set(Object.values(BosBrand));
  return brands.filter((item) => validValues.has(item.value as BosBrand));
}

export const makeStockVehicleFilterOptions = (filterName: string, filterOptions: IFilterResult[]) => {
  const filter = filterOptions?.find((option) => option?.filterName === filterName);

  return (
    filter?.filterValues
      ?.filter((value) => {
        const isEmpty = value?.value === '' || value?.code === '';
        return !isEmpty;
      })
      ?.map((value) => {
        return {
          value: getValue(value?.value),
          label: value?.code,
        };
      }) || []
  );
};

export const filterBrandOptions = (brandOptions: IBrandOption[], filterOptions: IFilterResult[]) => {
  const filteredBrandOptions = makeStockVehicleFilterOptions('brand', filterOptions || []);
  const options = [];

  brandOptions.forEach((brandOption) => {
    const match = filteredBrandOptions.find((filteredOption) => filteredOption.label === brandOption.label);
    if (match) {
      options.push(brandOption);
    }
  });
  return options;
};

enum OPTION_TYPE {
  COLOR = 'COLOR',
  INTERIOR = 'UPHOLSTERY',
}

const mapStandardOptions = (options: IVehicleSearchOptions[]): IVehicleSearchOptions[] => {
  return options?.filter((item) => {
    return item?.isStandard && item?.quote === 'YES';
  });
};

const mapOptions = (options: IVehicleSearchOptions[]): IVehicleSearchOptions[] => {
  return options?.filter((item) => {
    return (
      !item?.isStandard &&
      item?.customerPriceExclVat > 0 &&
      item?.optionType !== OPTION_TYPE.COLOR &&
      item?.optionType !== OPTION_TYPE.INTERIOR
    );
  });
};

const mapColor = (options: IVehicleSearchOptions[]) => {
  const color = options?.find((option) => {
    return option?.optionType === OPTION_TYPE.COLOR;
  });
  return { sku: color?.optionCode ?? '', name: color?.optionName ?? '' };
};

const mapInterior = (options: IVehicleSearchOptions[]) => {
  const interior = options?.find((option) => {
    return option?.optionType === OPTION_TYPE.INTERIOR;
  });
  return { sku: interior?.optionCode ?? '', name: interior?.optionName ?? '' };
};

export const mapStockData = (
  data: IVehicleSearchResponse,
  validDealerIds?: string[],
  vehicleReservedStatus?: boolean
): IDynamicListStockData[] => {
  return data?.foundVehicles?.map((vehicle: IReducedVehicleResponse) => {
    const color = mapColor(vehicle?.configuration?.options);
    const interior = mapInterior(vehicle?.configuration?.options);
    const customerPrice = defaultTo(vehicle?.prices?.totalCustomerPriceInclVatAndTax, 0);
    const campaignPrice = defaultTo(vehicle?.prices?.totalCampaignCustomerPriceIncVatAndTax, 0);
    return {
      merke: defaultTo(vehicle?.configuration?.brand, ''),
      modell: defaultTo(vehicle?.configuration?.modelId, ''),
      farge: defaultTo(color?.name, ''),
      fargeCode: defaultTo(color?.sku, ''),
      fomattedFarge: color,
      interior: defaultTo(interior?.name, ''),
      interiorCode: defaultTo(interior?.sku, ''),
      formattedInterior: interior,
      brukskode: defaultTo(vehicle?.applicationCode, ''),
      regNr: defaultTo(vehicle?.identification?.registrationNumber, ''),
      kmStand: `${formatNumber(vehicle?.usedVehicleData?.mileage || 0)} km`,
      regDato: formatDate(defaultTo(vehicle?.usedVehicleData?.firstTimeRegistrationDate, null)),
      bruktimport: defaultTo(vehicle?.vehicleCondition, null),
      forhandler: defaultTo(vehicle?.logistics?.locationId, ''),
      lagerdager: defaultTo(vehicle?.usedVehicleData?.daysOnStock, null),
      pris: customerPrice,
      serienummer: defaultTo(vehicle?.identification?.serialNumber?.toString(), ''),
      vin: defaultTo(vehicle?.identification?.vin, ''),
      arsmodell: defaultTo(vehicle?.usedVehicleData?.modelYear, null),
      totalpris: customerPrice,
      option: defaultTo(vehicle?.configuration?.options, null),
      previewImage: defaultTo(vehicle?.previewImage, null),
      brand: defaultTo(vehicle?.configuration?.brand, ''),
      modelDescription: defaultTo(vehicle?.configuration?.modelDescription, ''),
      availabilityCode: defaultTo(vehicle?.logistics?.availabilityCode, ''),
      locationName: defaultTo(vehicle?.logistics?.location, ''),
      status: vehicle?.logistics?.status,
      ...(vehicle?.logistics?.nextReserveStatus && { nextReserveStatus: vehicle?.logistics?.nextReserveStatus }),
      isOpen: false,
      oemOrderNo: defaultTo(vehicle?.logistics?.oemNumber, ''),
      modelSeries: vehicle?.configuration?.modelSeries,
      productionDate: formatDate(defaultTo(vehicle?.logistics?.actualProductionDate, '')),
      campaignData: defaultTo(vehicle?.prices?.campaignData, null),
      campaignExists: customerPrice !== campaignPrice,
      campaignPrice,
      fin: defaultTo(vehicle?.identification?.fin, null),
      trackTraceCode: defaultTo(vehicle?.logistics?.trackTraceCode, null),
      pricedEquipment: mapOptions(vehicle?.configuration?.options),
      standardEquipment: mapStandardOptions(vehicle?.configuration?.options),
      isDisabled: isVehicleDisabled(vehicle, validDealerIds, vehicleReservedStatus),
      statusName: getStatusByTTCode(vehicle?.logistics?.trackTraceCode, vehicle?.configuration?.brand),
    };
  });
};

export const isDealerMatched = (item: IStockFilterOptions, dealers?: Dealer[], filterKey?: string) => {
  if (filterKey !== 'locationIds') return false;

  return dealers?.some((dealer) => dealer?.dealerId === item?.label || dealer?.dealerName === item?.value);
};

export const getValue = (value: string | undefined | null) => {
  if (typeof value !== 'string') return '';
  const [first, second] = value.split('^');
  return second || first || '';
};

export const makeFilterOptions = (filterName: string, filterOptions: IFilterResult[], dealers?: Dealer[]) => {
  const filter = filterOptions?.find((option) => option?.filterName === filterName);

  return (
    filter?.filterValues
      ?.filter((value) => {
        const isEmpty = value?.value === '' || value?.code === '';
        const isNewCondition = filterName === 'vehicleCondition' && (value?.value === 'NEW' || value?.code === 'NEW');

        return !isEmpty && !isNewCondition;
      })
      ?.map((value) => {
        const isDisabled =
          filterName === 'locationId' &&
          isDealerMatched({ label: value?.code, value: value?.value }, dealers, 'locationIds');

        return {
          value: getValue(value?.value),
          label: value?.code,
          ...(isDisabled && { isDisabled: true }),
        };
      }) || []
  );
};

export const handleEmployeeLocation = (locations: string[], dealerIds: string[] = []) => {
  return Array.from(new Set([...locations, ...dealerIds]));
};

export const mapValues = (options: { value: string; label: string }[]) => {
  return options?.map((option) => option?.label);
};

export const createFilterRequestData = (
  filter: IStockFilterConfig,
  openedDropdown: string,
  dealerIds: string[],
  order: IOrder,
  isNewVehicle: boolean,
  modelSeriesMappingData?: IModelSeriesMapping[],
  isReserveFilterEnabled?: boolean
) => {
  const data: Record<string, any> = {};

  const filterKeyToExclude = dropdownToFilterKeyMap[openedDropdown];

  if (filterKeyToExclude !== 'brand') {
    data.brand = mapValues(filter?.brands ?? []);
  }
  if (filterKeyToExclude !== 'modelId') {
    data.modelId = mapValues(filter?.modelIds ?? []);
  }
  if (filterKeyToExclude !== 'colorCode') {
    data.colorCode = mapValues(filter?.colorIds ?? []);
  }
  if (filterKeyToExclude !== 'interiorCode') {
    data.interiorCode = mapValues(filter?.interiorIds ?? []);
  }
  if (filterKeyToExclude !== 'options') {
    data.options = mapValues(filter?.optionIdsIncluded ?? []);
  }
  if (filterKeyToExclude !== 'applicationCode') {
    data.applicationCode = mapValues(filter?.applicationCodeList ?? []);
  }
  if (filterKeyToExclude !== 'vehicleCondition') {
    data.vehicleCondition = mapValues(filter?.vehicleCondition ?? []);
  }
  if (filterKeyToExclude !== 'locationId') {
    const selected = mapValues(filter?.locationIds ?? []);
    data.locationId = isNewVehicle ? [...new Set(selected)] : handleEmployeeLocation(selected, dealerIds);
  }

  data.status = getVehicleListStatusFilter(isReserveFilterEnabled);

  data.filterTypes = getActiveFilterKeys(filter, filterKeyToExclude, dropdownToFilterKeyMap);

  if (isNewVehicle) {
    data.modelSeries = [getIFSModelSeriesCodeFromPimCode(order?.lead?.modelSeries, modelSeriesMappingData)];
  }
  return { filters: data };
};

export const updateTTCodeName = (filterData, openedDropdown: string, make: string) => {
  if (openedDropdown !== 'ttCode') {
    return filterData;
  }

  const ttCodeFilter = filterData.find(({ filterName }) => filterName === 'trackTraceCodes');

  if (!ttCodeFilter) {
    return filterData;
  }

  const filterValuesWithNames = ttCodeFilter.filterValues?.map((filter) => ({
    ...filter,
    value: getTTCodeStatusName(make, filter?.code),
  }));

  return filterData.map((data) =>
    data.filterName === 'trackTraceCodes' ? { ...data, filterValues: filterValuesWithNames } : data
  );
};

export const createOrderByRequestData = (
  orderBy: SortOrder[],
  direction: SortDirection,
  orderByFieldName: OrderByField
): SortOrder[] => {
  let orderByValue = orderBy;
  const method = direction ? direction?.toString()?.toUpperCase() : SortDirection?.Desc?.toString()?.toUpperCase();

  orderByValue = orderBy?.map((orderValue, index) => {
    if (orderValue?.fieldName === orderByFieldName) {
      return {
        ...orderValue,
        order: 1,
        method: getEnumKeyFromValue(Order, Order[method]),
      };
    } else {
      return { ...orderValue, order: (index || 1) + 1 };
    }
  });

  const isSortingEnabled = orderByValue?.find((orderValue) => orderValue?.fieldName === orderByFieldName);
  if (!isSortingEnabled) {
    orderByValue = [
      ...orderByValue,
      {
        order: 1,
        fieldName: orderByFieldName,
        method: getEnumKeyFromValue(Order, Order[method]),
      },
    ];
  }

  return orderByValue;
};

// Fetch strategy for non-Kia brands
export const createNonKiaStrategy = ({
  selectVehicleFilters,
  hasFetchedModelVariantCounts,
  applicationCodes,
  order,
  modelSeriesMappingData,
}: FilterFetchStrategyParams): FilterFetchStrategy => {
  return {
    shouldFetch() {
      if (!selectVehicleFilters?.brand || !selectVehicleFilters?.modelSeries) {
        return null;
      }

      // Phase 1: fetch model and variant counts
      if (!hasFetchedModelVariantCounts) {
        return { step: FILTER_FETCH_STEP.MODEL_VARIANT };
      }

      // Phase 2: fetch other filters
      if (selectVehicleFilters?.modelId || selectVehicleFilters?.pimSku) {
        return { step: FILTER_FETCH_STEP.ALL_FILTERS };
      }

      return null;
    },

    buildPayload(step) {
      if (step === FILTER_FETCH_STEP.MODEL_VARIANT) {
        return createFilterCountsPayload(
          selectVehicleFilters,
          applicationCodes,
          order,
          modelSeriesMappingData,
          null,
          true
        );
      }

      return createFilterCountsPayload(selectVehicleFilters, applicationCodes, order, modelSeriesMappingData);
    },
  };
};

// Fetch strategy for Kia filter counts
// exact match overrides based on factory equipment levels when modelId/pimSku is selected
export const createKiaStrategy = ({
  selectVehicleFilters,
  applicationCodes,
  order,
  modelSeriesMappingData,
  dataFromFactoryEquipLevels,
}: FilterFetchStrategyParams): FilterFetchStrategy => {
  const modelId = dataFromFactoryEquipLevels?.modelCodes?.[0];
  const skus = dataFromFactoryEquipLevels?.skus;

  return {
    shouldFetch() {
      if (
        selectVehicleFilters?.brand &&
        selectVehicleFilters?.modelSeries &&
        selectVehicleFilters?.modelId &&
        selectVehicleFilters?.pimSku &&
        !!modelId &&
        !isEmpty(skus)
      ) {
        return { step: FILTER_FETCH_STEP.ALL_FILTERS };
      }
      return null;
    },

    buildPayload() {
      return createFilterCountsPayload(
        selectVehicleFilters,
        applicationCodes,
        order,
        modelSeriesMappingData,
        dataFromFactoryEquipLevels
      );
    },
    getExactMatchOverrides() {
      return {
        modelId: dataFromFactoryEquipLevels?.modelCodes?.[0],
        skus: dataFromFactoryEquipLevels?.skus ?? [],
      };
    },
  };
};

/**
 * Generates exact match code(s) for a vehicle configuration.
 *
 * @param params.selectVehicleFilters - Selected vehicle filters.
 * @param params.modelIdOverride - Optional model ID override.
 * @param params.extraSkus - Optional extra SKUs.
 * @param params.structuredVehicle - Optional structured vehicle data.
 * @returns Array of exact match code strings.
 */
export const buildExactMatchCodeBase = ({
  selectVehicleFilters,
  modelIdOverride,
  extraSkus = [],
  structuredVehicle,
}: {
  selectVehicleFilters: IVehicleSelectedFilters;
  modelIdOverride?: string;
  extraSkus?: string[];
  structuredVehicle?: any;
}): string[] => {
  const { brand, colorCode: colorId, interiorCode, modelId, options } = selectVehicleFilters;
  let priceOptions = options ? options.split('^') : [];
  if (structuredVehicle && (colorId || interiorCode)) {
    const salesPackage = structuredVehicle?.salesPackages?.[0];
    // If color or interior is selected, check if the price is greater than 0 and if so, include it in priceoptions
    if (salesPackage) {
      const maybeAppend = (code: string | undefined, list: any[] | undefined, key: string) => {
        if (!code) return;
        const price = list?.find((item) => item[key] === code)?.additionalPriceExcludingVat ?? 0;
        if (price > 0) {
          priceOptions = [...priceOptions, code];
        }
      };
      maybeAppend(colorId, salesPackage.colors, 'varnishCode');
      maybeAppend(interiorCode, salesPackage.interiors, 'interiorCode');
    }
  }
  const baseModelId = modelIdOverride ?? modelId;
  if (extraSkus?.length) {
    return extraSkus.map((sku) =>
      generateExactMatchCode({
        brand,
        colorId,
        interiorCode,
        modelId: baseModelId,
        priceOptions: [...priceOptions, sku].join('^'),
      })
    );
  }
  return [
    generateExactMatchCode({
      brand,
      colorId,
      interiorCode,
      modelId: baseModelId,
      priceOptions: priceOptions.join('^'),
    }),
  ];
};

function buildModelVariantCountsPayload(
  selectedFilters: IVehicleSelectedFilters,
  applicationCodes: string[],
  modelSeriesCode: string
): { filters: IVehicleCountsPayload } {
  const data = {
    vehicleCondition: [VehicleCondition.NEW],
    applicationCode: applicationCodes,
    hiddenInNext: false,
    status: [VehicleStockStatus.FREE, VehicleStockStatus.DEMO],
    brand: [],
    modelSeries: [],
    filterTypes: ['modelId', 'pimSku'],
  } as IVehicleCountsPayload;
  if (selectedFilters?.brand) data.brand.push(selectedFilters.brand);
  if (modelSeriesCode) data.modelSeries.push(modelSeriesCode);
  return { filters: data };
}

/**
 * Builds a payload object for vehicle counts based on selected filters, application codes,
 * model series code, brand-specific logic (Kia), and optionally factory equipment levels.
 *
 * @param selectedFilters - The filters selected by the user for vehicle search.
 * @param applicationCodes - List of application codes to filter vehicles.
 * @param modelSeriesCode - The model series code to filter vehicles.
 * @param isKia - Flag indicating if the brand is Kia, which triggers brand-specific overrides.
 * @param dataFromFactoryEquipLevels - Optional factory equipment levels containing model codes and SKUs.
 * @returns An object containing the constructed vehicle counts payload as `filters`.
 */
function buildModelIdOrPimSkuPayload(
  selectedFilters: IVehicleSelectedFilters,
  applicationCodes: string[],
  modelSeriesCode: string,
  isKia: boolean,
  dataFromFactoryEquipLevels?: IUniqueModelCodeAndSkus | null
): { filters: IVehicleCountsPayload } {
  const data: IVehicleCountsPayload = {
    vehicleCondition: [VehicleCondition.NEW],
    applicationCode: applicationCodes,
    hiddenInNext: false,
    status: [VehicleStockStatus.FREE, VehicleStockStatus.DEMO],
    brand: [],
    modelSeries: [],
    modelId: [],
    pimSku: [],
    colorCode: [],
    interiorCode: [],
    options: [],
    filterTypes: [],
  };

  if (selectedFilters?.brand) data.brand.push(selectedFilters.brand);
  if (modelSeriesCode) data.modelSeries.push(modelSeriesCode);
  if (selectedFilters?.colorCode) data.colorCode.push(selectedFilters.colorCode);
  if (selectedFilters?.interiorCode) data.interiorCode.push(selectedFilters.interiorCode);

  // Brand-specific override for Kia
  if (isKia) {
    data.modelId = dataFromFactoryEquipLevels?.modelCodes?.filter(Boolean) ?? [];
    data.options = dataFromFactoryEquipLevels?.skus?.filter(Boolean) ?? [];
  } else {
    if (selectedFilters?.modelId) data.modelId.push(selectedFilters.modelId);
    if (selectedFilters?.pimSku) data.pimSku.push(selectedFilters.pimSku);
    if (selectedFilters?.options) {
      data.options.push(...(selectedFilters.options?.split('^') ?? []));
    }
  }

  // Remove empty arrays for dynamic keys
  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length === 0) {
      delete data[key as keyof IVehicleCountsPayload];
    }
  });

  // Dynamic filterTypes
  const BASE_FILTERS: VehicleFilterType[] = ['locationId', 'exactMatchCode', 'colorCode', 'interiorCode', 'options'];
  if (isKia) {
    BASE_FILTERS.push('pimSku');
  }
  data.filterTypes = BASE_FILTERS;

  return { filters: data };
}

export const createFilterCountsPayload = (
  selectedFilters: IVehicleSelectedFilters,
  applicationCodes: string[],
  order: IOrder | null,
  modelSeriesMappingData: IModelSeriesMapping[],
  dataFromFactoryEquipLevels?: IUniqueModelCodeAndSkus | null,
  isForModelVariantCounts: boolean = false
): { filters: IVehicleCountsPayload } => {
  const isKia = invokeProvidedBrandSpecificCode(MAKE.Kia, { order });
  const modelSeriesCode = selectedFilters?.modelSeries
    ? getIFSModelSeriesCodeFromPimCode(selectedFilters?.modelSeries, modelSeriesMappingData)
    : '';

  if (isForModelVariantCounts) {
    return buildModelVariantCountsPayload(selectedFilters, applicationCodes, modelSeriesCode);
  }

  if (selectedFilters?.modelId || selectedFilters?.pimSku) {
    return buildModelIdOrPimSkuPayload(
      selectedFilters,
      applicationCodes,
      modelSeriesCode,
      isKia,
      dataFromFactoryEquipLevels
    );
  }
};

// Maps an array of filter count results into an object keyed by filter name, with each value being an array of code/count pairs
export function mapFilterCountsData(filterResults: IFilterResultWithCount[]): IVehicleFiltersWithCounts {
  return filterResults?.reduce(
    (
      acc: IVehicleFiltersWithCounts,
      filter: { filterName: string | number; filterValues: IFilterValueWithCount[] }
    ) => {
      acc[filter.filterName] = (filter.filterValues ?? []).map((item: IFilterValueWithCount) => ({
        code: String(item.code),
        count: item.count,
        value: item?.value ?? null,
      }));
      return acc;
    },
    {} as IVehicleFiltersWithCounts
  );
}

export const getCountForFilterName = (countsArr: IVehicleFiltersWithCounts, filterName: string, code: string): number =>
  countsArr?.[filterName]?.find((item: FilterCount) => item?.code === code)?.count ?? 0;

export const getVariantCountForKia = (countsArr: IVehicleFiltersWithCounts): number =>
  countsArr?.['pimSku']?.reduce((acc, item: FilterCount) => acc + (item?.count ?? 0), 0) ?? 0;

export const getVehicleListStatusFilter = (includeReserved = false) =>
  includeReserved ? [VehicleStockStatus.RESERVED, ...vehicleListStatus] : vehicleListStatus;
